import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useSnackbar } from 'notistack';
import Button from '../shared/Button';
import useAPI from '../utils/api';
import { useUser } from '../utils/contexts';

const SUPPORT_EMAIL = 'support@hellobar.com';

// How often the page asks whether the site has been decided, so the dashboard unlocks without a
// reload: often while the website check runs (seconds), less often while a person reviews it
export const POLL_INTERVALS = { pending: 5 * 1000, in_review: 60 * 1000, rejected: 60 * 1000 };

// A check that hasn't finished within a minute is waiting on the background workers, so the page
// then asks less often instead of loading every site of the user every few seconds
export const PENDING_FAST_FOR = 60 * 1000;
export const PENDING_SLOW_INTERVAL = 30 * 1000;

// @param waited how long the page has already been asking, in ms
export const pollDelay = (status, waited) =>
  status === 'pending' && waited >= PENDING_FAST_FOR ? PENDING_SLOW_INTERVAL : POLL_INTERVALS[status];

// review_status from the API. A new site, or one whose address just changed, is pending while its
// website check runs; a site whose domain couldn't be found waits in review for an admin, who can
// reject it. Until it's approved the dashboard is locked: see SitePage.
export const isLocked = (site) => Object.keys(POLL_INTERVALS).includes(site?.review_status);

const SupportLink = () => <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>;

// The review emails go to the site's owner, who may not be the person looking at the dashboard
const whoWeEmail = (site, user) =>
  site.current_role === 'owner' && user?.email ? <strong>{user.email}</strong> : 'the site owner';

// The site's address as people say it: bakery.com rather than https://bakery.com/
const displayDomain = (site) => site.url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

const ICON_STYLE = { fontSize: 48 };

const NOTICES = {
  pending: {
    icon: <CircularProgress size={44} />,
    title: 'Checking your website…',
    message: (site) => (
      <Typography>
        We're making sure <strong>{displayDomain(site)}</strong> is live. This only takes a few seconds, and this
        page will update by itself.
      </Typography>
    )
  },
  in_review: {
    icon: <ManageSearchIcon color="primary" sx={ICON_STYLE} />,
    title: "Thanks for adding your website: we're taking a quick look",
    message: (site, user) => (
      <Typography>
        Our team is reviewing <strong>{displayDomain(site)}</strong>. We'll email {whoWeEmail(site, user)} within
        24 hours, once the review is finished.
      </Typography>
    )
  },
  rejected: {
    icon: <ReportProblemOutlinedIcon color="warning" sx={ICON_STYLE} />,
    title: "We couldn't verify your website",
    message: (site) => (
      <React.Fragment>
        <Typography>
          We tried to reach <strong>{displayDomain(site)}</strong> but couldn't confirm it's a live website. This
          usually happens when the address has a typo, the website isn't live yet, or the domain has expired.
        </Typography>
        <Typography sx={{ mt: 1.5 }}>Double-check the address below, and we'll check it again right away.</Typography>
      </React.Fragment>
    )
  }
};

// Lets the owner of a rejected site enter a different address, which is checked again right away
const UpdateAddressForm = ({ site }) => {
  const api = useAPI();
  const [url, setUrl] = useState(site.url);
  const [error, setError] = useState();
  const [saving, setSaving] = useState(false);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(undefined);
    const [ok, response] = await api.updateSite({ id: site.id, url });
    setSaving(false);

    if (ok) {
      site.update(response); // now pending, so the card switches to the website check
    } else {
      setError(response?.message || "We couldn't save this address. Please check it and try again.");
    }
  };

  return (
    <Box component="form" onSubmit={save} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 3, textAlign: 'left' }}>
      <TextField
        label="Website address"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        error={Boolean(error)}
        helperText={error}
        size="small"
        fullWidth
      />
      <Button
        type="submit"
        color="primary"
        loading={saving}
        disabled={!url || url === site.url}
        sx={{ flexShrink: 0, height: 40, px: 2.5, whiteSpace: 'nowrap' }} // one line, as tall as the small text field
      >
        Check again
      </Button>
    </Box>
  );
};

// Takes the place of the dashboard pages while the site isn't approved: one card that says why the
// dashboard is locked and what happens next
export default function SiteUnderReview({ site }) {
  const api = useAPI();
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const status = site.review_status;
  const notice = NOTICES[status];

  useEffect(() => {
    let timer;
    let stopped = false;
    let waited = 0;

    const schedule = () => {
      const delay = pollDelay(status, waited);
      waited += delay;
      timer = setTimeout(poll, delay);
    };

    async function poll() {
      try {
        const [ok, profile] = await api.profile();
        const latest = ok && profile.sites?.find((candidate) => candidate.id === site.id);
        if (latest && latest.review_status !== status) {
          if (latest.review_status === 'approved') {
            enqueueSnackbar(`${latest.url} is approved. Your dashboard is ready.`, { variant: 'success' });
          }
          user.update(profile); // App picks the updated site out of the profile; this effect restarts
          return;
        }
      } catch (_error) {
        // A failed request (e.g. a network blip) is simply tried again next time
      }
      if (!stopped) {
        schedule();
      }
    }

    schedule();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id, status]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: { xs: 4, md: 10 } }}>
      <Paper elevation={0} role="status" sx={{ maxWidth: 520, width: '100%', p: { xs: 3, md: 5 }, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>{notice.icon}</Box>
        <Typography component="h2" variant="h5" sx={{ fontWeight: 600, textTransform: 'none', mb: 2 }}>
          {notice.title}
        </Typography>
        {notice.message(site, user)}

        {status === 'rejected' && <UpdateAddressForm site={site} />}

        {status !== 'pending' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {status === 'rejected' ? 'Think we got it wrong?' : 'Questions?'} Email us at <SupportLink />.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
